// power/laundry.js — the laundry price-spike hold, as the panel shows it.
//
// HA owns the behaviour (ha-power/packages/laundry_spike_hold.yaml): during
// an Amber spike an idle washer or dryer is switched off at the plug, a
// running one is switched off when its run ends, and a running dryer is
// never cut. This module only reads that state and words it, for the
// home-screen line (with Run anyway) and the power card.
//
// Plain .js so pwa/watch-set.js can import the entity list and the model
// can be unit-tested under node.

import { fmtClock } from './model.js';

export const LAUNDRY = {
  spike: 'binary_sensor.2_wallace_street_balmain_price_spike',
  washerHeld: 'input_boolean.laundry_washer_held',
  dryerHeld: 'input_boolean.laundry_dryer_held',
  washerRunning: 'binary_sensor.laundry_washer_running',
  dryerRunning: 'binary_sensor.laundry_dryer_running',
  override: 'input_boolean.laundry_spike_override',
};
export const LAUNDRY_ENTITIES = Object.values(LAUNDRY);

const on = (states, id) => states?.[id]?.state === 'on';

// null when the hold package is not in HA (no helpers), so nothing renders.
// `spikeEnd` comes from the power advice: when Amber expects the spike to end.
export function readLaundry(states, spikeEnd) {
  if (!states?.[LAUNDRY.washerHeld] || !states?.[LAUNDRY.dryerHeld]) return null;
  const spike = on(states, LAUNDRY.spike);
  const override = on(states, LAUNDRY.override);
  const washer = { held: on(states, LAUNDRY.washerHeld), running: on(states, LAUNDRY.washerRunning) };
  const dryer = { held: on(states, LAUNDRY.dryerHeld), running: on(states, LAUNDRY.dryerRunning) };
  return { spike, override, washer, dryer, line: line(spike, override, washer, dryer, spikeEnd) };
}

// The home-screen line: null unless the hold is doing something someone
// should know about. Shown while a plug is held off (including the ten
// minutes after a spike before HA switches it back on) and while a spike
// is waiting on a running machine.
function line(spike, override, washer, dryer, spikeEnd) {
  if (override && !washer.held && !dryer.held) return null;
  const waiting = (m) => spike && !override && !m.held && m.running;
  if (!washer.held && !dryer.held && !waiting(washer) && !waiting(dryer)) return null;

  const until = !spike ? 'until the price settles' : spikeEnd ? `until about ${fmtClock(spikeEnd)}` : 'until the spike passes';
  const parts = [];
  if (washer.held && dryer.held) parts.push(`washer and dryer off ${until}`);
  else {
    if (washer.held) parts.push(`washer off ${until}`);
    else if (waiting(washer)) parts.push('washer off when this wash ends');
    if (dryer.held) parts.push(`dryer off ${until}`);
    else if (waiting(dryer)) parts.push('dryer still running');
  }
  const sub = parts.join('; ');
  return {
    text: 'Laundry on hold',
    sub: sub[0].toUpperCase() + sub.slice(1),
    canRunAnyway: spike && !override,
  };
}
